import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Instagram, Facebook, Mail, Clock, Heart } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { RESTAURANT } from "../data/menu";
import { Wordmark } from "./Logo";
import { PanAfricanRibbon } from "./Ribbon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. Welcome to the Aradelá family.");
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-black text-white relative" data-testid="footer">
      <PanAfricanRibbon className="top-0 left-0 right-0 h-1.5" />

      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <span className="text-[#D4A017] uppercase tracking-[0.3em] text-xs font-impact">
              Newsletter
            </span>
            <h3 className="font-impact uppercase text-3xl md:text-4xl mt-2 tracking-wide">
              Stay in the <span className="text-[#D4A017]">loop</span>
            </h3>
            <p className="text-white/55 text-sm mt-2 max-w-md">
              New dishes, events and stories — straight to your inbox.
            </p>
          </div>
          <form
            onSubmit={subscribe}
            className="flex w-full rounded-full overflow-hidden border border-white/15 bg-white/5"
            data-testid="newsletter-form"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent px-5 py-3.5 text-sm text-white placeholder:text-white/40 focus:outline-none"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-[#D4A017] hover:bg-[#F0BE3F] text-black px-6 font-impact uppercase text-sm tracking-wider disabled:opacity-60 transition-colors"
              data-testid="newsletter-submit"
            >
              {loading ? "…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      {/* Main info row */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-3">
          <Wordmark size="xl" />
          <p className="text-white/55 text-sm mt-5 leading-relaxed">
            {RESTAURANT.taglineLong}
          </p>
        </div>

        <div className="md:col-span-3 flex gap-3">
          <div className="w-10 h-10 rounded-full bg-[#D4A017]/15 flex items-center justify-center shrink-0">
            <MapPin size={16} className="text-[#D4A017]" />
          </div>
          <div className="text-sm text-white/80 leading-snug">
            <div className="font-impact uppercase text-white text-xs tracking-wider mb-1">
              Visit Us
            </div>
            19 Stratford Road, Wolverton,<br />Milton Keynes, MK12 5LJ
          </div>
        </div>

        <div className="md:col-span-2 flex gap-3">
          <div className="w-10 h-10 rounded-full bg-[#D4A017]/15 flex items-center justify-center shrink-0">
            <Phone size={16} className="text-[#D4A017]" />
          </div>
          <div>
            <div className="font-impact uppercase text-white text-xs tracking-wider mb-1">
              Call
            </div>
            <a href={`tel:${RESTAURANT.phoneRaw}`} className="text-white text-base font-bold hover:text-[#D4A017]">
              {RESTAURANT.phone}
            </a>
          </div>
        </div>

        <div className="md:col-span-2 flex gap-3">
          <div className="w-10 h-10 rounded-full bg-[#D4A017]/15 flex items-center justify-center shrink-0">
            <Clock size={16} className="text-[#D4A017]" />
          </div>
          <div className="text-xs text-white/80 leading-snug space-y-0.5">
            <div className="font-impact uppercase text-white tracking-wider mb-1">
              Hours
            </div>
            <div>Mon 4pm – 11pm</div>
            <div>Tue – Sat 12pm – 11pm</div>
            <div>Sun 1pm – 7pm</div>
          </div>
        </div>

        <div className="md:col-span-2">
          <p className="font-script text-[#D4A017] text-3xl md:text-4xl leading-tight">
            Thank you for<br />supporting local!
          </p>
          <div className="flex items-center gap-1 mt-2">
            <Heart size={14} fill="#006400" stroke="#006400" />
            <Heart size={14} fill="#FFFFFF" stroke="#FFFFFF" />
            <Heart size={14} fill="#006400" stroke="#006400" />
          </div>
        </div>
      </div>

      {/* Quick links + socials */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex flex-wrap items-center gap-x-7 gap-y-2 justify-center">
            {[
              { to: "/", label: "Home" },
              { to: "/menu", label: "Menu" },
              { to: "/about", label: "About" },
              { to: "/gallery", label: "Gallery" },
              { to: "/contact", label: "Contact" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-white/65 hover:text-[#D4A017] text-xs uppercase tracking-[0.2em] font-impact"
                data-testid={`footer-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <a
              href={RESTAURANT.social.instagram}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-full bg-white/5 border border-white/15 flex items-center justify-center hover:bg-[#D4A017] hover:text-black hover:border-[#D4A017] transition-all"
              data-testid="footer-instagram"
              aria-label="Instagram"
            >
              <Instagram size={14} />
            </a>
            <a
              href={RESTAURANT.social.facebook}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-full bg-white/5 border border-white/15 flex items-center justify-center hover:bg-[#D4A017] hover:text-black hover:border-[#D4A017] transition-all"
              data-testid="footer-facebook"
              aria-label="Facebook"
            >
              <Facebook size={14} />
            </a>
            <a
              href={RESTAURANT.social.tiktok}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-full bg-white/5 border border-white/15 flex items-center justify-center hover:bg-[#D4A017] hover:text-black hover:border-[#D4A017] transition-all"
              data-testid="footer-tiktok"
              aria-label="TikTok"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9a8.16 8.16 0 0 0 4.77 1.52V7.1a4.85 4.85 0 0 1-1.84-.41z"/>
              </svg>
            </a>
            <a
              href="mailto:info@aradelarestaurant.co.uk"
              className="w-9 h-9 rounded-full bg-white/5 border border-white/15 flex items-center justify-center hover:bg-[#D4A017] hover:text-black hover:border-[#D4A017] transition-all"
              data-testid="footer-email"
              aria-label="Email"
            >
              <Mail size={14} />
            </a>
          </div>
        </div>
        <div className="text-center text-white/35 text-[11px] pb-5 px-5">
          © {new Date().getFullYear()} Aradelá Nigerian Restaurant. Crafted with passion in Wolverton, Milton Keynes.
        </div>
      </div>
    </footer>
  );
};
